/**
 * Shared desktop primitives.
 *
 * Everything an AntD component already does is left to AntD — this file only
 * holds the page furniture (wrapper, page header, brand hero, A4 sheet) and the
 * few formatting helpers the desktop pages repeat. Money/date formatters are
 * imported from components/kit so both builds format a figure identically.
 */
import { Breadcrumb, Card, Flex, Space, Statistic, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

/**
 * Page canvas — ONE for every screen.
 *
 * Width and padding do not vary by page: a card's outer edge has to land in the
 * same place whichever page you are on, or moving between them shifts the
 * margins and reads as a layout fault. Home is the benchmark. A form that would
 * otherwise run too wide is constrained INSIDE its card (`.cid-measure`), never
 * by shrinking the page around it.
 *
 * `w` is still accepted so an old call site cannot break the alignment — it
 * resolves to the same canvas (see desktop.css).
 */
export const Wrap = ({ children, w }) => (
  <div className={`cid-wrap${w ? ' ' + w : ''}`}>{children}</div>
);

/**
 * Page header — breadcrumb, title, sub, and a right-aligned action group.
 * `crumbs`: [{ label, to }]  ·  `extra`: array of nodes.
 */
export function PageHead({ title, sub, extra, crumbs }) {
  const nav = useNavigate();
  return (
    <div style={{ marginBottom: 16 }}>
      {crumbs && (
        <Breadcrumb
          style={{ marginBottom: 8, fontSize: 12 }}
          items={crumbs.map((c) => ({ title: c.to ? <a onClick={() => nav(c.to)}>{c.label}</a> : c.label }))}
        />
      )}
      <Flex align="flex-start" gap={16} wrap="wrap">
        <div style={{ minWidth: 0 }}>
          <Title level={4} style={{ margin: 0, letterSpacing: '-.02em' }}>{title}</Title>
          {sub && <Text type="secondary" style={{ fontSize: 12.5 }}>{sub}</Text>}
        </div>
        {extra && <Space style={{ marginLeft: 'auto' }} size={8} wrap>{extra}</Space>}
      </Flex>
    </div>
  );
}

/** Card + Statistic. The phone's hand-rolled tile, as the real component. */
export const StatCard = ({ label, value, sub, color, onClick, loading }) => (
  <Card size="small" hoverable={!!onClick} onClick={onClick} loading={loading} styles={{ body: { padding: '13px 15px' } }}>
    <Statistic
      title={<span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em' }}>{label}</span>}
      value={value}
      valueStyle={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.02em', color }}
    />
    {sub && <Text type="secondary" style={{ fontSize: 11.5 }}>{sub}</Text>}
  </Card>
);

/* Brand chrome AntD has no component for. */
export const Hero = ({ children, style }) => <div className="cid-hero" style={style}>{children}</div>;
export const HeroLabel = ({ children }) => <div className="cid-hero-lab">{children}</div>;
export const HeroBig = ({ children, style }) => <div className="cid-hero-big" style={style}>{children}</div>;
export const HeroSub = ({ children }) => <div className="cid-hero-sub">{children}</div>;
export const HeroDiv = () => <div className="cid-hero-div" />;
export const Paper = ({ children, style }) => <div className="cid-paper" style={style}>{children}</div>;

/**
 * Two-line cell: a label over a quieter sub-line.
 *
 * Both lines inherit `white-space` rather than setting it, so the cell decides:
 * inside a column declared `ellipsis: true` antd puts `nowrap` on the <td> and
 * each line clamps with its own ellipsis; in an ordinary column they wrap as
 * before. Without the inherit + overflow the inner divs lay out at their natural
 * width and run underneath the next column instead of being clipped by it.
 */
export const Two = ({ a, b }) => {
  const line = { whiteSpace: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: 12.5, ...line }}>{a}</div>
      {b && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, ...line }}>{b}</div>}
    </div>
  );
};

export const Mono = ({ children }) => <span className="mono" style={{ fontWeight: 600 }}>{children}</span>;
export const Money = ({ v, c }) => <span className="tnum" style={{ fontWeight: 600, color: c }}>{v}</span>;

/** One hand-rolled row inside a Card (the shared `.cid-row` affordance). */
export const Row2 = ({ onClick, children, style }) => (
  <div className="cid-row" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', ...style }}>
    {children}
  </div>
);

/** A listing photo, or the striped placeholder when there is none. */
export const Photo = ({ src, ratio = '16/10', r = 0, style, children }) => (
  <div className={`cid-ph${src ? '' : ' cid-ph-empty'}`} style={{ aspectRatio: ratio, borderRadius: r, ...style }}>
    {src ? <img src={src} alt="" loading="lazy" /> : children}
  </div>
);

/** Initials for an avatar — same rule the phone's kit uses. */
export const inits = (n) => String(n || '').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('');
